import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { allIcons } from "../../../assets";
import TextInput from "../../../components/TextInputs";
import { getAppointmentChat } from "../../../services/common/chatApiFn";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiDialogContent-root": {
    height: "400px",
  },
  "& .close-icon-btn": {
    position: "absolute",
    top: "5px",
    right: "5px",
  },
}));

const ChatBox = styled("div")(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  border: `1px solid ${theme.palette.divider}`,
  // borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  height: "100%",
}));

const Message = styled("div")(({ theme, isUser }) => ({
  display: "flex",
  justifyContent: isUser ? "flex-end" : "flex-start",
  marginBottom: theme.spacing(1),
}));

const MessageBubble = styled("div")(({ theme, isUser }) => ({
  maxWidth: "70%",
  padding: theme.spacing("8px 10px"),
  borderRadius: theme.spacing("8px"),
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: isUser ? theme.palette.common.white : theme.palette.text.primary,
}));

const ChatDialog = forwardRef(({ socket }, ref) => {
  const messageContainerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const userData = useSelector((state) => state.userData.user);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setDialogData(params);
      fetchAllChats(params._id);
    },
    closeDialog: () => setOpen(false),
  }));

  useEffect(() => {
    if (dialogData) {
      socket.emit("joinAppointmentChat", {
        appointmentId: dialogData?._id,
      });

      const handleReceiveMessage = (newMessage) => {
        if (newMessage.receiverId === userData?._id) {
          setMessages((prev) => [...prev, newMessage]);
        }
      };

      socket.on("newAppointmentMessage", handleReceiveMessage);

      return () => {
        socket.off("newAppointmentMessage", handleReceiveMessage);
      };
    }
  }, [socket, dialogData]);

  //   useEffect(() => {
  //     if (dialogData) {
  //       socket.emit("resetUnreadCount", {
  //         conversationId: dialogData?.conversationId,
  //         userId: userData._id,
  //         conversationType: "WorkerWithUser",
  //       });
  //     }
  //   }, [socket, dialogData]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchAllChats = async (appointmentId) => {
    try {
      setChatLoading(true);
      const response = await getAppointmentChat(appointmentId);
      if (response.data.status === "success") {
        setMessages(response.data.data.conversation);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setError(false);
      setChatLoading(false);
    }
  };

  const sendMessage = () => {
    if (error) return;
    if (!message) return;

    socket.emit("sendAppointmentMessage", {
      appointmentId: dialogData?._id,
      senderId: userData?._id,
      receiverId: dialogData?.userId?._id,
      message,
    });

    setMessages((prev) => [
      ...prev,
      {
        appointmentId: dialogData?._id,
        senderId: userData?._id,
        receiverId: dialogData?.userId?._id,
        message,
      },
    ]);
    setMessage("");
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      scroll="body"
    >
      <IconButton className="close-icon-btn" onClick={handleClose}>
        <CloseIcon />
      </IconButton>

      <DialogTitle
        variant="h1"
        fontWeight={700}
        fontSize={"1.5rem"}
        fontFamily={"inter"}
        sx={{ m: 0, p: 2, textAlign: "center" }}
        id="customized-dialog-title"
      >
        {t("liveSupport")}
      </DialogTitle>
      <DialogContent dividers>
        <ChatBox ref={messageContainerRef}>
          {chatLoading ? (
            <Stack
              height={"100%"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress />
            </Stack>
          ) : (
            messages.map((msg, index) => (
              <Message key={index} isUser={msg.senderId === userData?._id}>
                <MessageBubble isUser={msg.senderId === userData?._id}>
                  <Typography variant="body2">{msg.message}</Typography>
                  <Typography
                    fontSize={"0.65rem"}
                    alignSelf={
                      msg.senderId === userData?._id ? "flex-end" : "flex-start"
                    }
                  >
                    {moment(msg.createdAt).format("MM/DD/YY hh:mm a")}
                  </Typography>
                </MessageBubble>
              </Message>
            ))
          )}
        </ChatBox>
      </DialogContent>
      <DialogActions>
        <TextInput
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("typeYourMessage")}
          onkeydown={(e) => e.key === "Enter" && sendMessage()}
          InputEndIcon={
            <Box ml={1} sx={{ cursor: "pointer" }} onClick={sendMessage}>
              <img src={allIcons.sendIcon} alt="send-icon" width={20} />
            </Box>
          }
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default ChatDialog;
